import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { useDynamicStats } from "@/hooks/use-dynamic-stats";
import { useCountUp } from "@/hooks/use-count-up";

export function HeroSection() {
  const { t } = useI18n();
  const { soldCount, years, clients } = useDynamicStats();

  const soldCounter = useCountUp({ end: soldCount, duration: 2000, startFrom: 80 });
  const yearsCounter = useCountUp({ end: years, duration: 1500 });
  const clientsCounter = useCountUp({ end: clients, duration: 2000 });

  // Função para realizar o scroll suave até o ID da seção
  const scrollToStock = () => {
    const stockSection = document.getElementById("stock");
    if (stockSection) {
      stockSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background z-10" />

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/20 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary/10 rounded-full blur-3xl animation-delay-200 animate-pulse-glow" />
      </div>

      {/* Content */}
      <div className="container relative z-20 px-4 py-20 text-center">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Logo badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-sm animate-fade-in">
            <span className="font-bold text-primary">PassCar</span>
            <span className="text-muted-foreground">Automóveis Premium
 </span>
          </div>

          {/* Main heading */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in animation-delay-100">
            <span className="text-foreground">{t.hero.title.split(" ").slice(0, -2).join(" ")}</span>{" "}
            <span className="text-primary">{t.hero.title.split(" ").slice(-2).join(" ")}</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto animate-fade-in animation-delay-200 whitespace-pre-line">
            {t.hero.subtitle}
          </p>

          {/* CTA Button */}
          <div className="flex items-center justify-center animate-fade-in animation-delay-300">
            <Button variant="outline" size="lg" className="text-lg px-8 py-6 gap-2" onClick={scrollToStock}>
              <Search className="h-5 w-5" />
              {t.hero.search}
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 pt-12 max-w-lg mx-auto animate-fade-in animation-delay-400">
            <div className="text-center" ref={soldCounter.ref}>
              <div className="text-3xl md:text-4xl font-bold text-primary">{soldCounter.count}+</div>
              <div className="text-sm text-muted-foreground mt-1">{t.hero.statsVehicles}</div>
            </div>
            <div className="text-center" ref={yearsCounter.ref}>
              <div className="text-3xl md:text-4xl font-bold text-primary">{yearsCounter.count}+</div>
              <div className="text-sm text-muted-foreground mt-1">{t.hero.statsYears}</div>
            </div>
            <div className="text-center" ref={clientsCounter.ref}>
              <div className="text-3xl md:text-4xl font-bold text-primary">{clientsCounter.count}+</div>
              <div className="text-sm text-muted-foreground mt-1">{t.hero.statsClients}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce"></div>
    </section>);
}